/**
 * This is the non-light version of the mime lib and supports:
 * (800+ MIME types, 1,000+ extensions).
 *
 * If we want to optimize a few bytes consider using the light version:
 * import { lookup } from 'mime/lite';
 */
import { getType } from 'mime';

/**
 * List of uncommon or incorrect mimetypes not identified with `mime` package that we want to override.
 * We still want to identify these during transfers or elsewhere within a
 * specific display category.
 *
 * This should not be exported!
 * MIMETYPE_OVERRIDES should only be access via util. As it does not provide
 * a complete picture of supported mimes.
 */
const MIMETYPE_OVERRIDES = {
  // Compressed audio file format used by Sony's IC digital audio recorders:
  dvf: 'audio/***',
  // Compressed audio file by Sony, MSV is a memory stick voice file
  msv: 'audio/***',
  // Raw image file types:
  arw: 'image/x-sony-arw',
  cr2: 'image/x-canon-cr2',
  crw: 'image/x-canon-crw',
  dcr: 'image/x-kodak-dcr',
  dng: 'image/x-adobe-dng',
  erf: 'image/x-epson-erf',
  k25: 'image/x-kodak-k25',
  kdc: 'image/x-kodak-kdc',
  mef: 'image/*',
  mrw: 'image/x-minolta-mrw',
  nef: 'image/x-nikon-nef',
  orf: 'image/x-olympus-orf',
  pef: 'image/x-pentax-pef',
  raf: 'image/x-fuji-raf',
  raw: 'image/x-panasonic-raw',
  sr2: 'image/x-sony-sr2',
  srf: 'image/x-sony-srf',
  x3f: 'image/x-sigma-x3f',
  psdt: 'drawing/*',
  indt: 'vector/*',
  // Spreadsheet
  pptx: 'application/vnd.ms-powerpoint',
  ai: 'image/application/vnd.adobe.illustrator' as const,
};

/**
 * Overrides the mimetype if the file extension is included in MIMETYPE_OVERRIDES
 *
 * @param fileName {string}
 * @returns mimetype {string} overriding mimetype for the file extension passed.
 */
export const getOverrideMimeType = (fileName: string): string | null => {
  const fileExtension = getFileExtension(fileName);

  if (!fileExtension) return null;
  if (!(fileExtension in MIMETYPE_OVERRIDES)) return null;
  if (!MIMETYPE_OVERRIDES[fileExtension]) return null;
  const overrideMimeType = MIMETYPE_OVERRIDES[fileExtension]; // .toLowerCase was already applied in `getFileExtension()`

  return overrideMimeType;
};

/**
 * Guess the mimetype from a file's extension.
 *
 * @param fileExtension {string}
 * @returns mimetype {string} matching mimetype for the file extension passed.
 */
export const getMimeTypeFromFileExtension = (
  fileExtension: string | undefined
): string | null => {
  if (!fileExtension) return null;
  const overrideMimeType =
    MIMETYPE_OVERRIDES[fileExtension as keyof typeof MIMETYPE_OVERRIDES]; // .toLowerCase was already applied in `getFileExtension()`
  return overrideMimeType ?? (getType(fileExtension) as string | null);
};

/**
 * Checks for a file extension in a filename.
 *
 * @param fileName {string}
 * @returns fileExtension {string | undefined}
 *
 *  returns undefined for:
 * - filenames which do not have a `.`
 * - start with `.` hidden file.
 * - end with `.`
 */
export const getFileExtension = (fileName = '') => {
  const fileExtensionIndex = fileName.lastIndexOf('.');
  if (fileExtensionIndex <= 0 || fileExtensionIndex === fileName.length)
    return undefined;

  return fileName.toLowerCase().slice(fileExtensionIndex + 1);
};

/**
 * - lowercases the filename
 * - extracts the extension (with no period)
 * - then uses the mimeType lookup to find a match.
 *
 * @param fileName {string}
 * @returns mimetype {string} if match returns string, otherwise returns undefined
 */
export const getMimeTypeFromFileName = (
  fileName = ''
): string | undefined | null => {
  const fileExtension = getFileExtension(fileName);
  if (!fileExtension) return undefined;
  return getMimeTypeFromFileExtension(fileExtension);
};

/**
 *
 * @param fileExtension {string}
 * @returns boolean returns true if the asset can be previewed.
 */
export const isFileExtensionPreviewable = (fileExtension = ''): boolean => {
  const inferredMimeType = getMimeTypeFromFileExtension(fileExtension);
  if (!inferredMimeType) return false;
  return (
    inferredMimeType.indexOf('video') === 0 ||
    inferredMimeType.indexOf('image') === 0
  );
};

/**
 * Checks if a filename has a file extension.
 *
 * A file extension is considered to be a dot followed by one or more alphanumeric characters at the end of the filename.
 *
 * @param filename - The filename to check for an extension. Can be undefined.
 * @returns `true` if the filename has an extension, `false` otherwise.
 * ```
 */
export function hasExtension(filename?: string) {
  if (!filename) {
    return false;
  }
  return /\.[a-zA-Z0-9]+$/.test(filename);
}
